import { useState, useEffect, useRef, useCallback } from 'react';
import { Cloud, CloudOff, Check, Loader2 } from 'lucide-react';

export const SyncStatus = ({ completedTasks, onSyncPull }) => {
  const [status, setStatus] = useState('loading');
  const debounceRef = useRef(null);
  const initialLoadDone = useRef(false);
  const skipNextPush = useRef(false);

  // Pull on mount
  useEffect(() => {
    const pull = async () => {
      try {
        const res = await fetch('/api/sync');
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (data.tasks && Object.keys(data.tasks).length > 0) {
          skipNextPush.current = true;
          onSyncPull(data.tasks);
        }
        setStatus('synced');
      } catch {
        setStatus('offline');
      }
      initialLoadDone.current = true;
    };
    pull();
  }, [onSyncPull]);

  // Auto-push on changes (debounced)
  const push = useCallback(async (tasks) => {
    setStatus('saving');
    try {
      const res = await fetch('/api/sync', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tasks }),
      });
      if (!res.ok) throw new Error();
      setStatus('synced');
    } catch {
      setStatus('offline');
    }
  }, []);

  const handleManualSync = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await fetch('/api/sync');
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.tasks && Object.keys(data.tasks).length > 0) {
        skipNextPush.current = true;
        onSyncPull(data.tasks);
      }
      setStatus('synced');
    } catch {
      setStatus('offline');
    }
  }, [onSyncPull]);

  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (skipNextPush.current) {
      skipNextPush.current = false;
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => push(completedTasks), 800);

    return () => clearTimeout(debounceRef.current);
  }, [completedTasks, push]);

  const icon = {
    loading: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
    saving: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
    synced: <Check className="w-3.5 h-3.5" />,
    offline: <CloudOff className="w-3.5 h-3.5" />,
  }[status];

  const label = {
    loading: 'Loading...',
    saving: 'Saving...',
    synced: 'Synced',
    offline: 'Offline',
  }[status];

  return (
    <button
      onClick={handleManualSync}
      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer bg-transparent border-none p-0"
      data-testid="sync-status"
      title={`Sync status: ${label} — Click to sync`}
    >
      <Cloud className="w-3.5 h-3.5" />
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
};
